import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { AdminApiService } from '../../services/admin-api.service';

interface EstimatorItem {
  id: string;
  surface: string;
  instanceId?: string | null;
  surfaceLabel: string;
  product: any;
  aiLength: number;
  aiWidth: number;
  aiAreaM2: number;
  manualLength: number;
  manualWidth: number;
  manualAreaM2: number;
  useManualSize: boolean;
  expanded: boolean;
  result: any;
  cartBoxQty?: number;
  boxAdjustmentNeeded?: boolean;
  sourceItems?: EstimatorItem[];
  maskUrl?: string | null;
  sliceUrl?: string | null;
  floorQuad?: any;
  settings?: any;
  polygon?: any;
  bbox?: any;
  xRatio?: number | null;
  yRatio?: number | null;
}

interface MobileSurfaceGroup {
  label: string;
  area: number;
  items: EstimatorItem[];
}

type MobileEstimatorPanel = 'products' | 'cart' | 'detail' | null;
type MobileProductStep = 'category' | 'series' | 'product';
type RoomManualDimensionField = 'length' | 'width' | 'height';
type EstimatorDimensionField = 'length' | 'width' | 'area';

@Component({
  selector: 'app-estimator',
  templateUrl: './estimatorpage.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  styleUrls: ['./estimatorpage.component.scss']
})
export class EstimatorpageComponent implements OnInit {
  readonly item: EstimatorItem | null = null;
  products: any[] = [];
  search = '';
  estimatorItems: EstimatorItem[] = [];
  totalQuantity = 0;
  generatedImageUrl: string | null = null;
  originalImageUrl: string | null = null;
  showOriginalImage = false;
  sourceFromVisualizer = false;
  mobileActivePanel: MobileEstimatorPanel = 'detail';
  mobileProductStep: MobileProductStep = 'category';
  selectedMobileCategoryKey = 'all';
  selectedMobileSeriesKey = 'all';
  openCartMenuItemId: string | null = null;
  returnConfirmOpen = false;
  roomManualLength = 0;
  roomManualWidth = 0;
  roomManualHeight = 0;
  roomManualSizeTouched = false;
  roomSizeSynced = true;
  private visualizerHotspots: any[] = [];
  private visualizerImageWidth: number | null = null;
  private visualizerImageHeight: number | null = null;

  constructor(
    private adminApiService: AdminApiService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadProducts();
  }

  private loadProducts(): void {
    this.adminApiService.getProductsEstimator().subscribe({
      next: (response: any) => {
        this.products = response?.data || response || [];
        this.loadFromVisualizerOrDefault();
      },
      error: (error) => {
        console.error('Gagal memuat produk estimator:', error);
        this.loadFromVisualizerOrDefault();
      }
    });
  }

  private loadFromVisualizerOrDefault(): void {
    const rawPayload = localStorage.getItem('frantinco_estimator_payload');

    if (rawPayload) {
      try {
        const payload = JSON.parse(rawPayload);
        this.generatedImageUrl = payload?.generatedImageUrl || null;
        this.originalImageUrl = payload?.originalImageUrl || payload?.originalUrl || payload?.sourceImageUrl || null;
        this.visualizerHotspots = Array.isArray(payload?.hotspots) ? payload.hotspots : [];
        this.visualizerImageWidth = Number(payload?.imageWidth || payload?.image?.width || 0) || null;
        this.visualizerImageHeight = Number(payload?.imageHeight || payload?.image?.height || 0) || null;
        this.showOriginalImage = false;
        this.sourceFromVisualizer = true;
        const visualizerProducts = Array.isArray(payload?.items) ? payload.items : [];

        this.estimatorItems = visualizerProducts
          .map((item: any, index: number) => {
            const visualizerProduct = item.product || item;
            const matchedProduct = this.findEstimatorProduct(item);
            if (!visualizerProduct && !matchedProduct) {
              return null;
            }

            return this.createEstimatorItem({
              product: this.mergeProductData(visualizerProduct, matchedProduct),
              surface: item.surface || `surface-${index + 1}`,
              instanceId: item.instanceId || null,
              surfaceLabel: item.surfaceLabel || item.surface || `Produk ${index + 1}`,
              expanded: index === 0,
              aiLength: item.aiLength || item.length || item.detectedLength || item.dimension?.length || 0,
              aiWidth: item.aiWidth || item.width || item.detectedWidth || item.dimension?.width || 0,
              aiAreaM2: item.areaM2 || item.aiAreaM2 || item.surfaceAreaM2 || 0,
              maskUrl: item.maskUrl || null,
              sliceUrl: item.sliceUrl || null,
              floorQuad: item.floorQuad || item.perspectiveQuad || null,
              settings: item.settings || null,
              polygon: item.polygon || null,
              bbox: item.bbox || null,
              xRatio: Number(item.xRatio),
              yRatio: Number(item.yRatio)
            });
          })
          .filter(Boolean) as EstimatorItem[];

        if (this.estimatorItems.length > 0) {
          this.syncRoomManualSizeFromItems();
          this.recalculateAll();
          return;
        }
      } catch (error) {
        console.error('Payload estimator tidak valid:', error);
      }
    }

    if (this.products.length > 0) {
      this.estimatorItems = [
        this.createEstimatorItem({
          product: this.products[0],
          surface: 'manual',
          surfaceLabel: 'Produk Manual',
          expanded: true,
          aiLength: 0,
          aiWidth: 0,
          aiAreaM2: 0
        })
      ];
      this.syncRoomManualSizeFromItems();
      this.updateTotalQuantity();
    }
  }

  private mergeProductData(primary: any, fallback: any): any {
    return {
      ...(fallback || {}),
      ...(primary || {}),
      coverage: primary?.coverage ?? fallback?.coverage,
      coverageType: primary?.coverageType ?? primary?.coverage_type ?? fallback?.coverageType ?? fallback?.coverage_type,
      waste: primary?.waste ?? fallback?.waste,
      imageUrl: primary?.imageUrl ?? primary?.thumbnailUrl ?? fallback?.imageUrl ?? fallback?.thumbnailUrl,
      thumbnailUrl: primary?.thumbnailUrl ?? primary?.imageUrl ?? fallback?.thumbnailUrl ?? fallback?.imageUrl,
      textureUrl: primary?.textureUrl ?? fallback?.textureUrl,
      code: primary?.code ?? primary?.sku ?? fallback?.code ?? fallback?.sku,
      sku: primary?.sku ?? primary?.code ?? fallback?.sku ?? fallback?.code,
      name: primary?.name ?? primary?.product_name ?? fallback?.name ?? fallback?.product_name
    };
  }

  private findEstimatorProduct(item: any): any | null {
    const visualizerProduct = item?.product || item;
    const candidates = [
      visualizerProduct?.code,
      visualizerProduct?.sku,
      visualizerProduct?.name
    ]
      .filter(Boolean)
      .map((value: string) => String(value).toLowerCase().trim());

    return this.products.find((product) => {
      const productValues = [
        product?.code,
        product?.sku,
        product?.name,
        product?.product_name
      ]
        .filter(Boolean)
        .map((value: string) => String(value).toLowerCase().trim());

      return candidates.some((candidate: string) =>
        productValues.some((value: string) => value === candidate || value.includes(candidate) || candidate.includes(value))
      );
    }) || null;
  }

  private createEstimatorItem(config: {
    product: any;
    surface: string;
    instanceId?: string | null;
    surfaceLabel: string;
    expanded: boolean;
    aiLength?: number;
    aiWidth?: number;
    aiAreaM2?: number;
    cartBoxQty?: number;
    maskUrl?: string | null;
    sliceUrl?: string | null;
    floorQuad?: any;
    settings?: any;
    polygon?: any;
    bbox?: any;
    xRatio?: number | null;
    yRatio?: number | null;
  }): EstimatorItem {
    const aiLength = this.normalizePositiveNumber(config.aiLength || 0);
    const aiWidth = this.normalizePositiveNumber(config.aiWidth || 0);
    const aiAreaM2 = this.normalizePositiveNumber(config.aiAreaM2 || 0);

    return {
      id: `${config.surface}-${config.product?.id || config.product?.code || config.product?.sku || Date.now()}`,
      surface: config.surface,
      instanceId: config.instanceId || null,
      surfaceLabel: config.surfaceLabel,
      product: config.product,
      aiLength,
      aiWidth,
      aiAreaM2,
      manualLength: 0,
      manualWidth: 0,
      manualAreaM2: 0,
      useManualSize: true,
      expanded: config.expanded,
      result: null,
      cartBoxQty: config.cartBoxQty,
      maskUrl: config.maskUrl || null,
      sliceUrl: config.sliceUrl || null,
      floorQuad: config.floorQuad || null,
      settings: config.settings || null,
      polygon: config.polygon || null,
      bbox: config.bbox || null,
      xRatio: Number.isFinite(Number(config.xRatio)) ? Number(config.xRatio) : null,
      yRatio: Number.isFinite(Number(config.yRatio)) ? Number(config.yRatio) : null
    };
  }

  get filteredProducts(): any[] {
    const keyword = this.search.toLowerCase().trim();
    if (!keyword) {
      return this.products;
    }

    return this.products.filter((product) => {
      const text = [
        product.name,
        product.product_name,
        product.code,
        product.sku,
        product.series?.name,
        product.series?.category?.name,
        product.category,
        product.category_name
      ].filter(Boolean).join(' ').toLowerCase();
      return text.includes(keyword);
    });
  }

  get mobileCategoryCards(): Array<{ key: string; name: string; count: number; image: string | null }> {
    const grouped = new Map<string, { key: string; name: string; count: number; image: string | null }>();

    this.filteredProducts.forEach((product) => {
      const name = this.getMobileProductCategoryName(product);
      const key = this.getMobileProductCategoryKey(product);
      const existing = grouped.get(key);

      if (existing) {
        existing.count += 1;
        if (!existing.image) {
          existing.image = this.getProductImage(product);
        }
        return;
      }

      grouped.set(key, {
        key,
        name,
        count: 1,
        image: this.getProductImage(product)
      });
    });

    return [
      { key: 'all', name: 'Semua Kategori', count: this.filteredProducts.length, image: null },
      ...Array.from(grouped.values())
    ];
  }

  get mobileSeriesCards(): Array<{ key: string; name: string; count: number; image: string | null }> {
    const grouped = new Map<string, { key: string; name: string; count: number; image: string | null }>();

    this.mobileProductsForSelectedCategory.forEach((product) => {
      const name = this.getMobileProductSeriesName(product);
      const key = this.getMobileProductSeriesKey(product);
      const existing = grouped.get(key);

      if (existing) {
        existing.count += 1;
        if (!existing.image) {
          existing.image = this.getProductImage(product);
        }
        return;
      }

      grouped.set(key, {
        key,
        name,
        count: 1,
        image: this.getProductImage(product)
      });
    });

    return Array.from(grouped.values());
  }

  get selectedMobileCategoryLabel(): string {
    return this.mobileCategoryCards.find((category) => category.key === this.selectedMobileCategoryKey)?.name || 'Kategori';
  }

  get selectedMobileSeriesLabel(): string {
    return this.mobileSeriesCards.find((series) => series.key === this.selectedMobileSeriesKey)?.name || 'Seri';
  }

  get mobileProductPanelTitle(): string {
    if (this.mobileProductStep === 'category') {
      return 'Pilih kategori';
    }
    if (this.mobileProductStep === 'series') {
      return this.selectedMobileCategoryLabel;
    }
    return this.selectedMobileSeriesLabel;
  }

  get mobileProductsForSelectedCategory(): any[] {
    const products = this.filteredProducts;
    if (this.selectedMobileCategoryKey === 'all') {
      return products;
    }

    return products.filter((product) => this.getMobileProductCategoryKey(product) === this.selectedMobileCategoryKey);
  }

  get mobileProductsForSelectedSeries(): any[] {
    const products = this.mobileProductsForSelectedCategory;
    if (this.selectedMobileSeriesKey === 'all') {
      return [];
    }

    return products.filter((product) => this.getMobileProductSeriesKey(product) === this.selectedMobileSeriesKey);
  }

  get mobileSurfaceGroups(): MobileSurfaceGroup[] {
    const groups = new Map<string, MobileSurfaceGroup>();

    this.estimatorItems.forEach((item) => {
      const label = this.getSurfaceBadgeLabel(item);
      const key = this.getEstimatorSurfaceKey(item);
      const area = this.getMobileItemArea(item);
      const existing = groups.get(key);

      if (existing) {
        existing.area += area;
        existing.items.push(item);
        return;
      }

      groups.set(key, {
        label,
        area,
        items: [item]
      });
    });

    return Array.from(groups.values());
  }

  getSurfaceGroupPrimaryItem(group: MobileSurfaceGroup): EstimatorItem {
    return group.items[0];
  }

  hasMultiProductSurfaceGroup(group: MobileSurfaceGroup): boolean {
    return group.items.length > 1 && group.items.some((item) => item.surface !== 'manual');
  }

  getSurfaceGroupProductItems(group: MobileSurfaceGroup): EstimatorItem[] {
    if (group.items.length <= 1) {
      return group.items;
    }

    const mergedItems = new Map<string, EstimatorItem>();

    group.items.forEach((item) => {
      const key = this.getProductCartKey(item.product) || item.id;
      const existing = mergedItems.get(key);

      if (existing) {
        existing.sourceItems = [...(existing.sourceItems || [existing]), item];
        return;
      }

      mergedItems.set(key, {
        ...item,
        sourceItems: [item]
      });
    });

    return Array.from(mergedItems.values());
  }

  getSurfaceGroupTitle(group: MobileSurfaceGroup): string {
    if (this.hasMultiProductSurfaceGroup(group)) {
      return group.label;
    }
    const item = this.getSurfaceGroupPrimaryItem(group);
    return item.product?.name || item.product?.product_name || item.product?.code || group.label;
  }

  getSurfaceGroupCategory(group: MobileSurfaceGroup): string {
    if (this.hasMultiProductSurfaceGroup(group)) {
      return `${group.items.length} produk di surface ini`;
    }
    return this.getProductCategory(this.getSurfaceGroupPrimaryItem(group).product);
  }

  getSurfaceGroupDescription(group: MobileSurfaceGroup): string {
    if (this.hasMultiProductSurfaceGroup(group)) {
      return 'Perlu atur luas dan box per produk di keranjang.';
    }
    return this.getProductDescription(this.getSurfaceGroupPrimaryItem(group).product);
  }

  getSurfaceGroupProductName(item: EstimatorItem): string {
    return item.product?.name || item.product?.product_name || item.product?.code || item.product?.sku || 'Produk';
  }

  getSurfaceGroupBoxQty(group: MobileSurfaceGroup): number {
    return group.items.reduce((total, item) => total + this.getCartBoxQty(item), 0);
  }

  getSurfaceProductBoxQty(item: EstimatorItem): number {
    return this.getEstimatorItemSources(item).reduce((total, sourceItem) => total + this.getCartBoxQty(sourceItem), 0);
  }

  isSurfaceProductBoxAdjustmentNeeded(item: EstimatorItem): boolean {
    return this.getEstimatorItemSources(item).some((sourceItem) => {
      return this.hasMultiProductVisualizerSurface(sourceItem) && sourceItem.boxAdjustmentNeeded !== false;
    });
  }

  openSurfaceGroupAdjust(group: MobileSurfaceGroup): void {
    this.openCartMenuItemId = null;
    this.setMobilePanel('cart');
  }

  get totalSurfaceCount(): number {
    return this.mobileSurfaceGroups.length;
  }

  get totalEstimateArea(): number {
    return this.estimatorItems.reduce((total, item) => total + this.getMobileItemArea(item), 0);
  }

  get roomManualArea(): number {
    const length = this.normalizePositiveNumber(this.roomManualLength);
    const width = this.normalizePositiveNumber(this.roomManualWidth);
    return length > 0 && width > 0 ? length * width : 0;
  }

  get roomManualWallArea(): number {
    const length = this.normalizePositiveNumber(this.roomManualLength);
    const width = this.normalizePositiveNumber(this.roomManualWidth);
    const height = this.normalizePositiveNumber(this.roomManualHeight);
    return length > 0 && width > 0 && height > 0 ? 2 * (length + width) * height : 0;
  }

  get displayedRoomArea(): number {
    if (this.roomManualArea > 0) {
      return this.roomManualArea;
    }

    const floorItem = this.estimatorItems.find((item) => this.getEstimatorSurfaceKey(item) === 'lantai');
    const ceilingItem = this.estimatorItems.find((item) => this.getEstimatorSurfaceKey(item) === 'ceiling');
    const roomAreaItem = floorItem || ceilingItem;
    return roomAreaItem ? this.normalizePositiveNumber(this.getActiveArea(roomAreaItem)) : 0;
  }

  get roomTotalArea(): number {
    return this.roomManualArea;
  }

  get roomFloorCeilingHint(): string {
    const missing = this.getMissingRoomDimensionLabels(false);
    return missing.length ? '-' : '';
  }

  get roomWallHint(): string {
    const missing = this.getMissingRoomDimensionLabels(true);
    return missing.length ? '-' : '';
  }

  private getMissingRoomDimensionLabels(includeHeight: boolean): string[] {
    const missing: string[] = [];
    if (this.normalizePositiveNumber(this.roomManualLength) <= 0) {
      missing.push('panjang');
    }
    if (this.normalizePositiveNumber(this.roomManualWidth) <= 0) {
      missing.push('lebar');
    }
    if (includeHeight && this.normalizePositiveNumber(this.roomManualHeight) <= 0) {
      missing.push('tinggi');
    }
    return missing;
  }

  setMobilePanel(panel: MobileEstimatorPanel): void {
    this.mobileActivePanel = panel;
    this.openCartMenuItemId = null;
    if (panel === 'products') {
      this.mobileProductStep = 'category';
      this.selectedMobileCategoryKey = 'all';
      this.selectedMobileSeriesKey = 'all';
    }
  }

  selectMobileCategory(key: string): void {
    this.selectedMobileCategoryKey = key;
    this.selectedMobileSeriesKey = 'all';
    this.mobileProductStep = 'series';
  }

  selectMobileSeries(key: string): void {
    this.selectedMobileSeriesKey = key;
    this.mobileProductStep = 'product';
  }

  backToMobileProductCategories(): void {
    this.mobileProductStep = 'category';
    this.selectedMobileCategoryKey = 'all';
    this.selectedMobileSeriesKey = 'all';
  }

  backToMobileProductSeries(): void {
    this.mobileProductStep = 'series';
    this.selectedMobileSeriesKey = 'all';
  }

  getMobileItemArea(item: EstimatorItem | null | undefined): number {
    if (!item) {
      return 0;
    }
    if (item.sourceItems?.length) {
      return item.sourceItems.reduce((total, sourceItem) => total + this.getMobileItemArea(sourceItem), 0);
    }

    const coverage = this.getProductCoverageArea(item.product);
    if (item.cartBoxQty !== undefined) {
      const boxQty = Number(item.cartBoxQty);
      if (Number.isFinite(boxQty) && boxQty > 0 && coverage > 0) {
        return boxQty * coverage;
      }
      return 0;
    }

    const resultArea = this.normalizePositiveNumber(item.result?.roomSize);
    if (resultArea > 0) {
      return resultArea;
    }

    const inferredBoxQty = this.getCartBoxQty(item);
    if (inferredBoxQty > 0 && coverage > 0) {
      return inferredBoxQty * coverage;
    }

    return this.normalizePositiveNumber(this.getActiveArea(item));
  }

  getCartAreaInputValue(item: EstimatorItem): string {
    const area = this.normalizePositiveNumber(this.getMobileItemArea(item));
    return Number.isInteger(area) ? area.toString() : area.toFixed(2).replace(/\.?0+$/, '');
  }

  getCartBoxQty(item: EstimatorItem): number {
    if (item.sourceItems?.length) {
      return item.sourceItems.reduce((total, sourceItem) => total + this.getCartBoxQty(sourceItem), 0);
    }

    if (item.cartBoxQty !== undefined) {
      const uiQty = Number(item.cartBoxQty);
      return Number.isFinite(uiQty) && uiQty >= 0 ? uiQty : 0;
    }

    const uiQty = Number(item.cartBoxQty ?? 0);
    if (Number.isFinite(uiQty) && uiQty > 0) {
      return uiQty;
    }

    const resultQty = Number(item.result?.requiredQty ?? 0);
    return Number.isFinite(resultQty) && resultQty > 0 ? resultQty : 0;
  }

  private getMobileProductCategoryName(product: any): string {
    const category = this.getProductCategory(product);
    return category && category !== '-' ? category : 'Kategori';
  }

  private getMobileProductCategoryKey(product: any): string {
    return String(this.getMobileProductCategoryName(product) || 'kategori').toLowerCase().trim().replace(/\s+/g, '-');
  }

  private getMobileProductSeriesName(product: any): string {
    const series = this.getProductSeries(product);
    return series && series !== '-' ? series : 'Tanpa Seri';
  }

  private getMobileProductSeriesKey(product: any): string {
    return String(this.getMobileProductSeriesName(product) || 'series').toLowerCase().trim().replace(/\s+/g, '-');
  }

  addManualProduct(product: any): void {
    const productKey = this.getProductCartKey(product);
    const existingItem = this.estimatorItems.find((item) => this.getProductCartKey(item.product) === productKey);

    if (existingItem) {
      this.estimatorItems = this.estimatorItems.map((item) => ({
        ...item,
        expanded: item.id === existingItem.id
      }));
      this.updateTotalQuantity();
      return;
    }

    const item = this.createEstimatorItem({
      product,
      surface: 'manual',
      surfaceLabel: 'Produk Manual',
      expanded: true,
      aiLength: 0,
      aiWidth: 0,
      aiAreaM2: 0
    });

    this.estimatorItems = [
      ...this.estimatorItems.map((existing) => ({ ...existing, expanded: false })),
      item
    ];
    this.recalculateItem(item);
    this.updateTotalQuantity();
  }

  incrementCartBox(item: EstimatorItem): void {
    this.setCartBoxQtyForItem(item, this.getCartBoxQty(item) + 1);
    this.markItemBoxAdjusted(item);
    this.estimatorItems = [...this.estimatorItems];
    this.updateTotalQuantity();
  }

  decrementCartBox(item: EstimatorItem): void {
    const nextQty = Math.max(0, this.getCartBoxQty(item) - 1);
    this.setCartBoxQtyForItem(item, nextQty);
    this.markItemBoxAdjusted(item);
    this.estimatorItems = [...this.estimatorItems];
    this.updateTotalQuantity();
  }

  toggleCartItemMenu(item: EstimatorItem): void {
    this.openCartMenuItemId = this.openCartMenuItemId === item.id ? null : item.id;
  }

  changeCartItemProduct(item: EstimatorItem): void {
    this.openCartMenuItemId = null;
    this.setMobilePanel('products');
    this.search = '';
    this.selectedMobileCategoryKey = this.getMobileProductCategoryKey(item.product);
    this.selectedMobileSeriesKey = 'all';
    this.mobileProductStep = 'series';
  }

  adjustSurfaceProductArea(item: EstimatorItem): void {
    this.openCartMenuItemId = null;
    this.setMobilePanel('cart');
  }

  hasMultiProductVisualizerSurface(item: EstimatorItem): boolean {
    if (item.surface === 'manual') {
      return false;
    }
    const key = this.getEstimatorSurfaceKey(item);
    return this.estimatorItems.filter((existing) => existing.surface !== 'manual' && this.getEstimatorSurfaceKey(existing) === key).length > 1;
  }

  private getEstimatorItemSources(item: EstimatorItem): EstimatorItem[] {
    return item.sourceItems?.length ? item.sourceItems : [item];
  }

  private setCartBoxQtyForItem(item: EstimatorItem, qty: number): void {
    const sources = this.getEstimatorItemSources(item);
    const normalizedQty = Math.max(0, Math.round(this.normalizePositiveNumber(qty)));

    sources.forEach((sourceItem, index) => {
      sourceItem.cartBoxQty = index === 0 ? normalizedQty : 0;
    });
  }

  private markItemBoxAdjusted(item: EstimatorItem): void {
    const sourceItems = this.getEstimatorItemSources(item);
    const relatedItems = new Set<EstimatorItem>(sourceItems);

    sourceItems.forEach((sourceItem) => {
      const surfaceKey = this.getEstimatorSurfaceKey(sourceItem);
      const productKey = this.getProductCartKey(sourceItem.product);

      this.estimatorItems
        .filter((existingItem) => {
          return this.getEstimatorSurfaceKey(existingItem) === surfaceKey && this.getProductCartKey(existingItem.product) === productKey;
        })
        .forEach((existingItem) => relatedItems.add(existingItem));
    });

    relatedItems.forEach((sourceItem) => {
      sourceItem.boxAdjustmentNeeded = false;
    });
    this.roomSizeSynced = false;
  }

  private markMultiProductSurfacesNeedAdjustment(): void {
    this.estimatorItems.forEach((item) => {
      if (this.hasMultiProductVisualizerSurface(item)) {
        item.boxAdjustmentNeeded = true;
        item.cartBoxQty = 0;
      }
    });
    this.updateTotalQuantity();
  }

  onCartItemAreaInput(item: EstimatorItem, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const normalizedText = this.normalizeRoomDimensionText(input.value);
    if (input.value !== normalizedText) {
      input.value = normalizedText;
    }

    const sources = this.getEstimatorItemSources(item);
    const area = this.normalizePositiveNumber(normalizedText);

    sources.forEach((sourceItem, index) => {
      sourceItem.cartBoxQty = undefined;
      sourceItem.useManualSize = true;
      sourceItem.manualAreaM2 = index === 0 ? area : 0;
      this.recalculateItem(sourceItem);
    });

    this.markItemBoxAdjusted(item);
    this.estimatorItems = [...this.estimatorItems];
    this.updateTotalQuantity();
  }

  removeCartItem(item: EstimatorItem): void {
    const sourceItems = new Set(this.getEstimatorItemSources(item));
    this.openCartMenuItemId = null;
    if (sourceItems.size > 1) {
      this.estimatorItems = this.estimatorItems.filter((existing) => !sourceItems.has(existing));
      this.updateTotalQuantity();
      return;
    }

    const index = this.estimatorItems.findIndex((existing) => existing.id === item.id);
    if (index >= 0) {
      this.removeItem(index);
    }
  }

  removeItem(index: number): void {
    this.estimatorItems.splice(index, 1);
    this.estimatorItems = [...this.estimatorItems];
    this.updateTotalQuantity();
  }

  openReturnToVisualizerConfirm(): void {
    this.returnConfirmOpen = true;
  }

  closeReturnToVisualizerConfirm(): void {
    this.returnConfirmOpen = false;
  }

  get visualizerReturnItemCount(): number {
    return this.visualizerReturnItems.length;
  }

  confirmReturnToVisualizer(): void {
    const returnItems = this.visualizerReturnItems.map((item) => ({
      surface: item.surface,
      instanceId: item.instanceId || null,
      surfaceLabel: item.surfaceLabel,
      product: item.product,
      aiLength: item.aiLength || null,
      aiWidth: item.aiWidth || null,
      areaM2: item.aiAreaM2 || item.manualAreaM2 || null,
      maskUrl: item.maskUrl || null,
      sliceUrl: item.sliceUrl || null,
      floorQuad: item.floorQuad || null,
      settings: item.settings || null,
      polygon: item.polygon || null,
      bbox: item.bbox || null,
      xRatio: item.xRatio ?? null,
      yRatio: item.yRatio ?? null
    }));

    const payload = {
      generatedImageUrl: this.generatedImageUrl,
      originalImageUrl: this.originalImageUrl,
      imageWidth: this.visualizerImageWidth,
      imageHeight: this.visualizerImageHeight,
      hotspots: this.visualizerHotspots,
      items: returnItems
    };

    localStorage.setItem('frantinco_visualizer_return_payload', JSON.stringify(payload));
    localStorage.setItem('frantinco_estimator_payload', JSON.stringify(payload));
    this.returnConfirmOpen = false;
    this.router.navigate(['/visualizer']);
  }

  private get visualizerReturnItems(): EstimatorItem[] {
    return this.estimatorItems.filter((item) => item.surface !== 'manual');
  }

  toggleItem(item: EstimatorItem): void {
    item.expanded = !item.expanded;
  }

  onDimensionChange(item: EstimatorItem): void {
    item.manualLength = this.normalizePositiveNumber(item.manualLength);
    item.manualWidth = this.normalizePositiveNumber(item.manualWidth);
    item.manualAreaM2 = this.normalizePositiveNumber(item.manualAreaM2);
    this.recalculateItem(item);
    this.updateTotalQuantity();
  }

  formatEstimatorDimensionInput(value: number): string {
    return this.normalizePositiveNumber(value).toString();
  }

  onEstimatorDimensionInput(item: EstimatorItem, field: EstimatorDimensionField, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const normalizedText = this.normalizeRoomDimensionText(input.value);
    if (input.value !== normalizedText) {
      input.value = normalizedText;
    }

    const value = this.normalizePositiveNumber(normalizedText);
    if (field === 'length') {
      item.manualLength = value;
    } else if (field === 'width') {
      item.manualWidth = value;
    } else {
      item.manualAreaM2 = value;
    }

    this.onDimensionChange(item);
  }

  onMobileRoomDimensionChange(item: EstimatorItem): void {
    item.useManualSize = true;
    item.manualLength = this.normalizePositiveNumber(item.manualLength);
    item.manualWidth = this.normalizePositiveNumber(item.manualWidth);
    item.manualAreaM2 = 0;
    this.recalculateItem(item);
    this.updateTotalQuantity();
  }

  onRoomManualSizeChange(): void {
    this.roomManualSizeTouched = true;
    this.roomManualLength = this.normalizePositiveNumber(this.roomManualLength);
    this.roomManualWidth = this.normalizePositiveNumber(this.roomManualWidth);
    this.roomManualHeight = this.normalizePositiveNumber(this.roomManualHeight);
    this.roomSizeSynced = false;
  }

  private applyRoomManualSizeToEstimator(): void {
    this.estimatorItems.forEach((item) => {
      if (this.hasMultiProductVisualizerSurface(item)) {
        return;
      }

      item.useManualSize = true;
      item.manualLength = this.roomManualLength;
      item.manualWidth = this.roomManualWidth;
      item.manualAreaM2 = this.getRoomManualAreaForItem(item);
      this.recalculateItem(item);
    });

    this.updateTotalQuantity();
  }

  syncEstimatorWithRoomSize(): void {
    this.onRoomManualSizeChange();
    this.applyRoomManualSizeToEstimator();
    this.markMultiProductSurfacesNeedAdjustment();
    this.roomSizeSynced = true;
  }

  formatRoomDimensionInput(value: number): string {
    return this.normalizePositiveNumber(value).toString();
  }

  onRoomManualDimensionInput(field: RoomManualDimensionField, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }

    const normalizedText = this.normalizeRoomDimensionText(input.value);
    if (input.value !== normalizedText) {
      input.value = normalizedText;
    }

    const value = this.normalizePositiveNumber(normalizedText);
    if (field === 'length') {
      this.roomManualLength = value;
    } else if (field === 'width') {
      this.roomManualWidth = value;
    } else {
      this.roomManualHeight = value;
    }

    this.onRoomManualSizeChange();
  }

  private normalizeRoomDimensionText(rawValue: string): string {
    const cleanValue = String(rawValue || '')
      .replace(',', '.')
      .replace(/[^\d.]/g, '');
    const firstDotIndex = cleanValue.indexOf('.');
    const value = firstDotIndex >= 0
      ? `${cleanValue.slice(0, firstDotIndex + 1)}${cleanValue.slice(firstDotIndex + 1).replace(/\./g, '')}`
      : cleanValue;

    if (!value) {
      return '0';
    }

    const [integerPart, decimalPart] = value.split('.');
    const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '') || '0';
    return decimalPart === undefined ? normalizedInteger : `${normalizedInteger}.${decimalPart}`;
  }

  private getRoomManualAreaForItem(item: EstimatorItem): number {
    if (this.isWallEstimatorItem(item)) {
      return this.roomManualWallArea;
    }
    return this.roomManualArea;
  }

  private isWallEstimatorItem(item: EstimatorItem): boolean {
    const surfaceText = `${item.surface || ''} ${item.surfaceLabel || ''}`.toLowerCase();
    return /\bwall\b/.test(surfaceText) || surfaceText.includes('dinding');
  }

  private getEstimatorSurfaceKey(item: EstimatorItem): string {
    const rawSurface = `${item.surface || ''} ${item.surfaceLabel || ''}`.toLowerCase();
    if (rawSurface.includes('dinding') || /\bwall\b/.test(rawSurface)) {
      return 'dinding';
    }
    if (rawSurface.includes('lantai') || /\bfloor\b/.test(rawSurface)) {
      return 'lantai';
    }
    if (rawSurface.includes('ceiling') || rawSurface.includes('plafon')) {
      return 'ceiling';
    }
    return String(item.surfaceLabel || item.surface || 'area').toLowerCase().trim();
  }

  private syncRoomManualSizeFromItems(): void {
    if (this.roomManualSizeTouched) {
      return;
    }

    const itemWithSize = this.estimatorItems.find((item) => {
      const length = this.normalizePositiveNumber(item.manualLength || item.aiLength);
      const width = this.normalizePositiveNumber(item.manualWidth || item.aiWidth);
      return length > 0 || width > 0;
    });

    this.roomManualLength = this.normalizePositiveNumber(itemWithSize?.manualLength || itemWithSize?.aiLength || 0);
    this.roomManualWidth = this.normalizePositiveNumber(itemWithSize?.manualWidth || itemWithSize?.aiWidth || 0);
  }

  onManualToggle(item: EstimatorItem): void {
    if (item.useManualSize) {
      item.manualLength = this.getActiveLength(item);
      item.manualWidth = this.getActiveWidth(item);
      item.manualAreaM2 = this.getActiveArea(item);
    }
    this.recalculateItem(item);
    this.updateTotalQuantity();
  }

  getActiveLength(item: EstimatorItem): number {
    return item.useManualSize ? item.manualLength : item.aiLength;
  }

  getActiveWidth(item: EstimatorItem): number {
    return item.useManualSize ? item.manualWidth : item.aiWidth;
  }

  getActiveArea(item: EstimatorItem): number {
    const manualArea = this.normalizePositiveNumber(item.manualAreaM2);
    const aiArea = this.normalizePositiveNumber(item.aiAreaM2);
    const length = this.getActiveLength(item);
    const width = this.getActiveWidth(item);

    if (item.useManualSize && manualArea > 0) {
      return manualArea;
    }
    if (!item.useManualSize && aiArea > 0) {
      return aiArea;
    }
    return length > 0 && width > 0 ? length * width : 0;
  }

  hasAiSize(item: EstimatorItem): boolean {
    return (item.aiLength > 0 && item.aiWidth > 0) || item.aiAreaM2 > 0;
  }

  recalculateAll(): void {
    this.estimatorItems.forEach((item) => this.recalculateItem(item));
    this.updateTotalQuantity();
  }

  recalculateItem(item: EstimatorItem): void {
    const desc = this.getProductDesc(item.product);
    if (!desc) {
      item.result = null;
      this.updateTotalQuantity();
      return;
    }

    const length = this.normalizePositiveNumber(this.getActiveLength(item));
    const width = this.normalizePositiveNumber(this.getActiveWidth(item));
    const activeArea = this.normalizePositiveNumber(this.getActiveArea(item));

    if (activeArea <= 0) {
      item.result = null;
      this.updateTotalQuantity();
      return;
    }

    const coverage = Number(desc.coverage ?? item.product?.coverage ?? 0);
    const waste = Number(desc.waste ?? item.product?.waste ?? 0);

    if (coverage <= 0) {
      item.result = null;
      this.updateTotalQuantity();
      return;
    }

    const multiplier = 1 + waste / 100;
    const requiredRaw = (activeArea * multiplier) / coverage;
    const requiredQty = Math.ceil(requiredRaw);

    item.result = {
      length,
      width,
      roomSize: activeArea,
      coverage,
      coverageType: desc.coverageType || desc.coverage_type || item.product?.coverageType || 'Box',
      waste,
      multiplier,
      requiredRaw,
      requiredQty,
      product: item.product
    };

    this.updateTotalQuantity();
  }

  private updateTotalQuantity(): void {
    this.totalQuantity = this.estimatorItems.reduce((total, item) => {
      return total + this.getEstimatedRequirementQty(item);
    }, 0);
  }

  get totalRequirementSummary(): string {
    const totalsByUnit = new Map<string, number>();

    this.estimatorItems.forEach((item) => {
      const qty = this.getEstimatedRequirementQty(item);
      if (qty <= 0) {
        return;
      }

      const unit = this.normalizeCoverageUnit(
        item.result?.coverageType ||
        this.getProductDesc(item.product)?.coverageType ||
        this.getProductDesc(item.product)?.coverage_type ||
        item.product?.coverageType ||
        'Box'
      );
      totalsByUnit.set(unit, (totalsByUnit.get(unit) || 0) + qty);
    });

    if (totalsByUnit.size === 0) {
      return '0';
    }

    return Array.from(totalsByUnit.entries())
      .map(([unit, qty]) => `${qty} ${unit}`)
      .join(' + ');
  }

  private getEstimatedRequirementQty(item: EstimatorItem): number {
    if (item.cartBoxQty !== undefined) {
      const cartQty = Number(item.cartBoxQty);
      return Number.isFinite(cartQty) && cartQty >= 0 ? cartQty : 0;
    }

    const resultQty = Number(item.result?.requiredQty ?? 0);
    if (Number.isFinite(resultQty) && resultQty > 0) {
      return resultQty;
    }

    const desc = this.getProductDesc(item.product);
    const coverage = Number(desc?.coverage ?? item.product?.coverage ?? 0);
    const waste = Number(desc?.waste ?? item.product?.waste ?? 0);
    const area = this.normalizePositiveNumber(this.getActiveArea(item));

    if (area <= 0 || coverage <= 0) {
      return 0;
    }

    return Math.ceil((area * (1 + waste / 100)) / coverage);
  }

  private normalizeCoverageUnit(unit: any): string {
    const normalized = String(unit || 'Box').trim();
    return normalized || 'Box';
  }

  private getProductCartKey(product: any): string {
    return String(product?.id || product?.code || product?.sku || product?.name || product?.product_name || '')
      .toLowerCase()
      .trim();
  }

  normalizePositiveNumber(value: any): number {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue) || numberValue < 0) {
      return 0;
    }
    return numberValue;
  }

  getProductDesc(product: any): any {
    return (
      product?.series?.descproducts?.[0] ||
      product?.series?.descProducts?.[0] ||
      product?.series?.desc_products?.[0] ||
      product?.descproducts?.[0] ||
      product?.descProducts?.[0] ||
      product?.desc_products?.[0] ||
      product
    );
  }

  getProductCoverageArea(product: any): number {
    const desc = this.getProductDesc(product);
    return this.normalizePositiveNumber(desc?.coverage ?? product?.coverage ?? 0);
  }

  getProductCategory(product: any): string {
    return product?.series?.category?.name || product?.category_name || product?.category || '-';
  }

  getProductSeries(product: any): string {
    return product?.series?.name || product?.series || '-';
  }

  getSurfaceBadgeLabel(item: EstimatorItem): string {
    const rawLabel = String(item.surfaceLabel || item.surface || 'Area').trim();
    const normalized = rawLabel.toLowerCase();
    if (normalized.includes('dinding') || /\bwall\b/.test(normalized)) {
      return 'DINDING';
    }

    const labelMap: Record<string, string> = {
      floor: 'Lantai',
      lantai: 'Lantai',
      wall: 'Dinding',
      dinding: 'Dinding',
      ceiling: 'Ceiling',
      plafon: 'Plafon',
      backsplash: 'Backsplash',
      manual: 'Manual'
    };

    return (labelMap[normalized] || rawLabel).toUpperCase();
  }

  getProductDescription(product: any): string {
    const desc = this.getProductDesc(product);
    return (
      product?.description ||
      product?.product_description ||
      product?.desc ||
      desc?.description ||
      desc?.product_description ||
      desc?.desc ||
      product?.series?.description ||
      'Deskripsi produk belum tersedia.'
    );
  }

  getProductSize(product: any): string {
    const desc = this.getProductDesc(product);
    if (!desc?.width || !desc?.height) {
      return '-';
    }
    return `${desc.width} ${desc.sizeType || desc.size_type || ''} x ${desc.height} ${desc.sizeType || desc.size_type || ''}`.trim();
  }

  getCoverageText(product: any): string {
    const desc = this.getProductDesc(product);
    if (!desc?.coverage) {
      return '-';
    }
    return `${desc.coverage} m² / ${desc.coverageType || desc.coverage_type || 'Box'}`;
  }

  getWasteText(product: any): string {
    const desc = this.getProductDesc(product);
    if (desc?.waste === null || desc?.waste === undefined) {
      return '-';
    }
    return `${desc.waste}%`;
  }

  hasWaste(product: any): boolean {
    return this.getWasteText(product) !== '-';
  }

  getWasteTooltip(product: any): string {
    const wasteText = this.getWasteText(product);
    return wasteText === '-'
      ? 'Waste product belum tersedia'
      : `Waste product ${wasteText} untuk cadangan potongan`;
  }

  getProductImage(product: any): string {
    const image =
      product?.productsimage?.[0]?.imageUrl ||
      product?.productImages?.[0]?.imageUrl ||
      product?.thumbnailUrl ||
      product?.image_url ||
      product?.imageUrl ||
      product?.image ||
      product?.thumbnail ||
      product?.photo;

    if (!image) {
      return '/uploads/others/no-image.png';
    }
    if (image.startsWith('http') || image.startsWith('blob:')) {
      return image;
    }
    if (image.startsWith('/')) {
      return image.substring(1);
    }
    return image;
  }

  clearVisualizerPayload(): void {
    localStorage.removeItem('frantinco_estimator_payload');
    this.generatedImageUrl = null;
    this.originalImageUrl = null;
    this.showOriginalImage = false;
    this.sourceFromVisualizer = false;
  }

  get displayedVisualizerImageUrl(): string | null {
    return this.showOriginalImage && this.originalImageUrl ? this.originalImageUrl : this.generatedImageUrl;
  }

  get visualizerImageToggleLabel(): string {
    return this.showOriginalImage ? 'Pasang' : 'Bersihkan';
  }

  toggleVisualizerImage(): void {
    if (!this.originalImageUrl || !this.generatedImageUrl) {
      return;
    }
    this.showOriginalImage = !this.showOriginalImage;
  }

  async downloadVisualizerPreview(): Promise<void> {
    const imageUrl = this.displayedVisualizerImageUrl;

    if (!imageUrl) {
      return;
    }

    const filename = `frantinco-estimator-preview-${Date.now()}.jpg`;
    let downloadUrl = imageUrl;
    let revokeUrl = false;

    try {
      if (imageUrl.startsWith('data:')) {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        downloadUrl = URL.createObjectURL(blob);
        revokeUrl = true;
      } else if (!imageUrl.startsWith('blob:')) {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        downloadUrl = URL.createObjectURL(blob);
        revokeUrl = true;
      }
    } catch (error) {
      console.warn('Gagal menyiapkan download preview estimator, memakai URL langsung:', error);
      downloadUrl = imageUrl;
      revokeUrl = false;
    }

    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = filename;
    anchor.click();

    if (revokeUrl) {
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
    }
  }

  get whatsappMessage(): string {
    const validItems = this.estimatorItems.filter((item) => item.result);
    const detailText = validItems
      .map((item, index) => {
        return `${index + 1}. ${item.surfaceLabel} - ${item.product?.name || item.product?.code || '-'}: ${item.result.requiredQty} ${item.result.coverageType} (${item.result.roomSize.toFixed(2)} m²)`;
      })
      .join('\n');

    return [
      `Halo Frantinco, saya ingin konsultasi estimasi kebutuhan produk. Total: ${this.totalQuantity} box/item.`,
      detailText || '-',
      this.generatedImageUrl ? `Gambar visualizer: ${this.generatedImageUrl}` : '',
      'Terima kasih.'
    ].filter(Boolean).join('\n');
  }

  get whatsappLink(): string {
    return `https://wa.me/6289666150888?text=${encodeURIComponent(this.whatsappMessage)}`;
  }

  get emailLink(): string {
    const subject = 'Konsultasi Estimasi Frantinco';
    return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(this.whatsappMessage)}`;
  }
}
